/**
 * ============================================================================
 * ScheduleReviewDialog — modal for scheduling a new management review (Phase 7).
 * Owner: Phase 7 — Management Review Module.
 *
 * Posts to the PG-backed POST /api/v1/management-reviews (drafts/61 F0-8), which
 * inserts a row into management_review.reviews at status SCHEDULED. The reporter
 * becomes the chair; the review period defaults to the calendar quarter that
 * contains the scheduled date (ICH Q10 §II.B quarterly cadence). On success the
 * calendar refetches.
 *
 * URS rows: MGR-001 (schedule), MGR-002 (quarterly cadence).
 * ============================================================================ */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { buildHeaders, apiUrl } from '../../api/client';
import styles from './ReviewCalendar.module.css';

export interface ScheduleReviewDialogProps {
  onClose: () => void;
  onCreated: (reviewId: string) => void;
}

// Default the date input to today (YYYY-MM-DD for the native date picker).
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ScheduleReviewDialog({ onClose, onCreated }: ScheduleReviewDialogProps) {
  const { t } = useTranslation();
  const [scheduledDate, setScheduledDate] = useState<string>(todayIso());
  const [title, setTitle] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  async function submit() {
    if (!scheduledDate) {
      setErrorMsg(
        t('mgr.schedule.missingDate', { defaultValue: 'A scheduled date is required.' }),
      );
      return;
    }
    setSubmitting(true);
    setErrorMsg(undefined);
    try {
      // Schedule at 09:00 UTC on the chosen day for a sensible default time.
      const scheduledAtUtc = new Date(`${scheduledDate}T09:00:00Z`).toISOString();
      const r = await fetch(apiUrl('/api/v1/management-reviews'), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify({
          scheduledAtUtc,
          title: title.trim() || undefined,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErrorMsg(
          (body as { detail?: string }).detail ??
            t('mgr.schedule.failed', { defaultValue: 'Schedule review failed.' }),
        );
        return;
      }
      const body = (await r.json()) as { id?: string; reviewId?: string };
      onCreated(body.reviewId ?? body.id ?? '');
    } catch (e) {
      setErrorMsg(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.dialogShade} role="dialog" aria-modal="true">
      <div className={styles.dialogBox}>
        <h2 className={styles.dialogTitle}>
          {t('mgr.schedule.title', { defaultValue: 'Schedule management review' })}
        </h2>
        <p className={styles.dialogSubtitle}>
          {t('mgr.schedule.subtitle', {
            defaultValue:
              'Opens a SCHEDULED review. The review period defaults to the quarter containing the scheduled date (ICH Q10 §II.B).',
          })}
        </p>

        <div className={styles.formField}>
          <label htmlFor="mgr-date">
            {t('mgr.schedule.dateLabel', { defaultValue: 'Scheduled date' })}
          </label>
          <input
            id="mgr-date"
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
          />
        </div>

        <div className={styles.formField}>
          <label htmlFor="mgr-title">
            {t('mgr.schedule.titleLabel', { defaultValue: 'Title (optional)' })}
          </label>
          <input
            id="mgr-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('mgr.schedule.titlePlaceholder', {
              defaultValue: 'Defaults to "Q{n} {year} Management Review"',
            })}
          />
        </div>

        {errorMsg && (
          <div className={styles.dialogError} role="alert">
            {errorMsg}
          </div>
        )}

        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting
              ? t('common.submitting', { defaultValue: 'Submitting…' })
              : t('mgr.schedule.submit', { defaultValue: 'Schedule review' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
